import { DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  Output,
} from '@angular/core';
import { MatChip } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EqualStatusBadgeWidthDirective } from '../../../../directives';
import { WidgetDataStore } from '../../../../stores';
import { TableRow } from '../../../table';
import { CardWidget, TableWidgetData } from '../../models';
import { WidgetID } from '../../models/widget.enum';

@Component({
  selector: 'siat-card-widget',
  imports: [
    MatIconModule,
    MatTooltipModule,
    TranslatePipe,
    DatePipe,
    NgClass,
    NgTemplateOutlet,
    MatChip,
    EqualStatusBadgeWidthDirective,
  ],
  templateUrl: './card-widget.component.html',
  styleUrl: './card-widget.component.scss',
})
export class CardWidgetComponent {
  private readonly CLOSED_STATUSES = ['COMPLETATA', 'CHIUSA'];
  private readonly FORCED_CLOSED_STATUSES = ['CHIUSURA_FORZATA'];
  private readonly SUSPENDED_STATUSES = ['SOSPESA'];
  private readonly ACTIVE_STATUSES = [
    'ATTIVA',
    'IN_CORSO',
    'APERTA',
    'ASSEGNATO',
    'NON_ASSEGNATO',
  ];
  @Input() lockedPatient: boolean;
  @Input() widget!: CardWidget;
  @Output() downloadElement = new EventEmitter<{
    idDocumento: string;
    fromMoniser: boolean;
  }>();
  @Output() goToDetail = new EventEmitter<TableRow>();

  private widgetDataStore = inject(WidgetDataStore);
  private translate = inject(TranslateService);

  tableData = computed(() => {
    const data = this.widgetDataStore.getWidgetData(this.widget?.id);
    return (
      (data as TableWidgetData) || {
        columns: [],
        rows: [],
        totalCount: 0,
        currentPage: 0,
        pageSize: 10,
      }
    );
  });

  get labels(): string[] {
    return this.widget?.config?.labels || [];
  }

  get fields(): string[] {
    return this.widget?.config?.fields || [];
  }

  get chipKey(): string {
    return this.widget?.config?.chipKey;
  }

  get stackedChips(): boolean {
    return !!this.widget?.config?.stackedChips;
  }

  get tableRows(): TableRow[] {
    return this.tableData()?.rows || [];
  }

  getFieldValue(row: TableRow, fieldIndex: number): string {
    const field = this.fields[fieldIndex];
    if (!field) return '';
    const value = field
      .split('.')
      .reduce(
        (obj: unknown, key) =>
          (obj as Record<string, unknown> | undefined)?.[key],
        row as unknown,
      );
    return (value as string) ?? '';
  }

  /**
   * Name shown for the row, followed by the configured code field in brackets when present.
   * @returns {string} The row name
   */
  getRowName(row: TableRow): string {
    const name = this.getFieldValue(row, 0);
    const codeField = this.widget?.config?.nameCodeField;
    const code = codeField ? row?.[codeField] : null;
    return code ? `${name} (${code})` : name;
  }

  private normalizeStatus(status: string): string {
    return status?.toUpperCase().replace(/\s+/g, '_') ?? '';
  }

  getPicStatusClass(status: string): string {
    if (!status) return '';
    const s = this.normalizeStatus(status);
    if (this.FORCED_CLOSED_STATUSES.includes(s)) return 'grey-chips';
    if (this.CLOSED_STATUSES.includes(s)) return 'grey-chips';
    if (this.SUSPENDED_STATUSES.includes(s)) return 'yellow-chips';
    if (this.ACTIVE_STATUSES.includes(s)) return 'green-chips';
    return 'grey-chips';
  }

  /*  getPicStatusLabel(status: string): string {
    if (!status) return '';
    const s = this.normalizeStatus(status);
    if (this.CLOSED_STATUSES.includes(s)) return 'Chiusa';
    if (this.SUSPENDED_STATUSES.includes(s)) return 'Sospesa';
    if (this.ACTIVE_STATUSES.includes(s)) return 'Attiva';
    return status;
  }*/

  // --- Click handlers ---

  /*
  onNameClick(row: TableRow): void {
    this.downloadElement.emit(row.idFile?.toString());
  }
*/

  onRowClick(row: TableRow): void {
    if (this.widget.config.path.download) {
      const downloadObj = {
        idDocumento: (row['idFile'] as { toString(): string })?.toString(),
        fromMoniser: row['fromMoniser'] as boolean,
      };

      this.downloadElement.emit(downloadObj);
    } else {
      this.goToDetail.emit(row);
    }
  }

  isDate(value: unknown): boolean {
    if (!value) return false;
    if (value instanceof Date) return true;
    if (typeof value !== 'string') return false;
    return /\d{4}-\d{2}-\d{2}/.test(value) && !isNaN(Date.parse(value));
  }

  /**
   * Sanitize date string to ensure it's in a valid format for parsing.
   */
  sanitizeDate(value: string): string {
    const normalized = value.replace(/Z+$/, '') + 'Z';

    return normalized;
  }

  protected readonly WidgetID = WidgetID;
}
